import React, { useState, useEffect } from 'react';
import { some, find } from 'lodash';
import { Link, useParams, useLocation } from 'react-router-dom';
import {
  Translate,
  TranslateHTML,
  withTranslate,
} from '../../../../../../core/translate';
import Modal, {
  ModalBody,
  ModalCustomContent,
  ModalHeader,
} from '../../../../../components/modal/Modal';
import StandardFeatures from '../../../../../components/standardFeatures/StandardFeatures';
import FetchTrimFeatures from '../FetchTrimFeatures';
import PowertrainOptions from '../../../../../components/powertrainOptions/PowertrainOptions';
import BottomNav, {
  BottomNavButtonControls,
  BottomNavContent,
  BottomNavMoreViews,
  BottomNavPrimaryButton,
} from '../../../../../components/bottomNav/BottomNav';
import {
  BYO_CLOSE,
  dispatchCustomEvent,
} from '../../../../../utils/customMessage';
import { Button } from '../../../../../../core/controls/button/Button';
import { SvgIcon } from '../../../../../../core/icons';
import OptionCard from '../../../../../components/cards/optionCard/OptionCard';
import CarouselNavigation from '../../../../../components/carouselNavigation/CarouselNavigation';
import LegalDisclosure from '../../../../../components/legalDisclosure/LegalDisclosure';
import { SimpleOptionInfo } from '../../../../../common/options/SimpleOptionCard';
import { Route, Routes, useNavigate } from 'react-router';
import SimpleOptionDetails from '../../../../../common/optionDetails/simpleOptionDetails/SimpleOptionDetails';
import MatchingKeyFeatures from '../../../../../components/matchingKeyFeatures/MatchingKeyFeatures';
import { useModelInfo } from '../../../../../common/ModelInfoContext';
import DriveTypeDetail from '../../../../../common/driveTypeDetail/DriveTypeDetail';
import ModalImage from '../../../../../components/modal/ModalImage';
import useLargeBreakpoint from '../../../../../hooks/useLargeBreakpoint';

const TrimFeaturesModal = (props) => {
  const [style, setStyle] = useState(null);
  const [trimInfo, setTrimInfo] = useState(null);
  const [trimHighlights, setTrimHighlights] = useState(null);
  const [navIndex, setNavIndex] = useState(null);
  const { search } = useLocation();
  const {
    onClose,
    onSelect,
    translate,
    trimGroups,
    features,
    isSelectionRevealed,
    selections,
    setBottomOffset,
    selectedKeyFeatureFilters = [],
    filteredGroups = [],
    isPreSelected = false,
  } = props;
  const { styleId } = useParams();
  const compareLink = `../../compare?selectedIndex=${navIndex}`;
  const { isEvMode } = useModelInfo();
  const { dimensions } = trimGroups[0];
  const dimensionsLabel = dimensions?.header;
  const largeImageUrl = dimensions?.largeImageUrl;
  const smallImageUrl = dimensions?.smallImageUrl;

  const isAtLeastLarge = useLargeBreakpoint();
  const dimensionTableImageUrl = isAtLeastLarge ? largeImageUrl : smallImageUrl;
  const isFiltered = new URLSearchParams(search).get('isFiltered');

  const groups =
    isFiltered && filteredGroups.length > 0 ? filteredGroups : trimGroups;

  const updateTrimHighlights = (styleId) => {
    const trim = find(groups, { styleId: styleId });
    const { highlights } = trim;

    setTrimHighlights(highlights.length > 0 ? highlights : null);
  };

  const updateStyle = (style) => {
    setStyle(style);
    updateTrimHighlights(style.styleId);
    setNavIndex(groups.findIndex((group) => group.styleId === style.styleId));
  };

  const styleIdInt = parseInt(styleId, 10);
  useEffect(() => {
    setStyle(find(groups, { styleId: styleIdInt }));
    updateTrimHighlights(styleIdInt);
    setNavIndex(groups.findIndex((group) => group.styleId === styleIdInt));
  }, []);

  useEffect(() => {
    if (style) {
      const { trimName } = style;
      setTrimInfo(find(groups, { trimName }));
    }
  }, [style]);

  const header = translate('trim.details');

  const slideData = groups.map((option, index) => {
    return {
      ...option,
      slideKey: `${option.trimName}_${index}`,
      primaryText: option.trimName,
    };
  });

  const trims = slideData.map(({ primaryText }) => primaryText);
  const baseTrim = trims.length > 0 ? trims[0] : null;
  const isBaseTrim = baseTrim === style?.trimName;
  const previousTrimName =
    trims.length > 0 ? trims[trims.indexOf(style?.trimName) - 1] : null;

  const carousel = (
    <CarouselNavigation
      analyticsKey="trim nav"
      displayPrice={true}
      slideData={slideData}
      navIndex={navIndex}
      smallLabelNext={translate('byo.page.general.next.step.v3')}
      labelNext={translate('byo-page.next.trim')}
      smallLabelPrev={translate('byo.page.general.prev.step.mobile.v3')}
      labelPrev={translate('byo-page.previous.trim')}
      onSlideChange={(style) => updateStyle(style)}
      modifier="trim-details"
    />
  );

  const selected =
    isSelectionRevealed &&
    some(trimInfo && trimInfo.options, { styleId: selections?.styleId });

  const navigate = useNavigate();

  const onDetails = (option) => {
    const optionPath = option.packageOption
      ? `package/${option.code}`
      : `option/${option.code}`;

    navigate(optionPath);
  };

  const onDetailsClose = () => {
    navigate('.');
  };

  const infoButtonAnalytics = `info:${trimInfo?.trimName}`;

  return (
    <>
      <Modal isOpen={true} contentLabel={header} onRequestClose={onClose}>
        <ModalHeader
          text={header}
          analyticsKey={trimInfo && trimInfo.trimName}
          onClose={onClose}
          contentUnderHeader={carousel}
        />

        <ModalBody
          className="trim-features"
          innerClassName="trim-features-modal-body__inner"
          onScroll={() => dispatchCustomEvent(BYO_CLOSE)}>
          <div className="trim-features-highlights">
            {!isEvMode && (
              <LegalDisclosure
                className="legal-disclosure--trim-details"
                showAsStatic
              />
            )}
            {trimHighlights && (
              <>
                <TranslateHTML
                  as="div"
                  className="trim-features-highlights__header"
                  value={
                    isBaseTrim
                      ? 'trim.features.highlights.header.base.v3'
                      : 'trim.features.highlights.header.v3'
                  }
                  trim={isBaseTrim ? baseTrim : previousTrimName}
                />
                <div className="trim-features-highlights-grid">
                  {trimHighlights.map((option) => (
                    <OptionCard
                      option={option}
                      image={option.smallImageUrl}
                      key={option.code}
                      className="trim-features-highlights__card"
                      disabled={true}>
                      <div className="simple-option__details">
                        <div
                          className="simple-option__description"
                          dangerouslySetInnerHTML={{
                            __html: option.description,
                          }}
                        />
                        {option.disclaimer || option.description ? (
                          <div className="simple-option__price-container">
                            <SimpleOptionInfo
                              option={option}
                              onDetails={onDetails}
                              analyticsKey={infoButtonAnalytics}
                            />
                          </div>
                        ) : null}
                      </div>
                    </OptionCard>
                  ))}
                </div>
              </>
            )}
            {selectedKeyFeatureFilters &&
              selectedKeyFeatureFilters.length > 0 &&
              style && (
                <section>
                  <h2 className="matching-key-features__header">
                    <Translate value="common.matching.key.features.v3" />
                  </h2>
                  <h3 className="matching-key-features__disclosure">
                    <Translate
                      value="feature.key.features.disclosure.v3"
                      trim={trimInfo?.trimName}
                    />
                  </h3>
                  <MatchingKeyFeatures
                    features={features}
                    selectedKeyFeatureFilters={selectedKeyFeatureFilters}
                    style={style}
                  />
                </section>
              )}
            {trimGroups.length > 1 && trimInfo?.compareAll && (
              <Button
                as={Link}
                to={compareLink}
                className="compare-options__button compare-options__button--trim stat-button-link"
                data-dtm={trimInfo && trimInfo.trimName}>
                <SvgIcon name="compare" className="button__icon--left" />
                <span className="compare-options__label">
                  <Translate value="trim.compare.all.trims.v3" />
                </span>
              </Button>
            )}
          </div>
          <div className="standard-features">
            {isEvMode ? (
              <>
                <TranslateHTML
                  className="trim-features-drive-types__header"
                  value="available.drive.types.v3"
                />
                <DriveTypeDetail />
                {dimensions && (
                  <div className="dimensions-table-container">
                    <h2 className="standard-features__header dimensions-table-header">
                      {dimensionsLabel}
                    </h2>
                    <ModalImage url={dimensionTableImageUrl} />
                  </div>
                )}
              </>
            ) : (
              <PowertrainOptions
                features={features}
                trimInfo={trimInfo}
                style={style}
                disclaimer={props.disclaimer}
              />
            )}

            <h2 className="standard-features__header">
              {translate('component.common.standard.features')}
            </h2>
            {style ? (
              <FetchTrimFeatures
                style={style}
                render={({ features }) => (
                  <StandardFeatures
                    features={features}
                    analyticsKey={`${trimInfo && trimInfo.trimName}:features`}
                  />
                )}
              />
            ) : null}
          </div>
        </ModalBody>
        <ModalCustomContent>
          {({ setIsClosing, onCloseModal }) => (
            <BottomNav
              navClass={'trim-details__bottom-nav'}
              setBottomOffset={setBottomOffset}
              containerClass={'trim-details__bottom-nav-container'}>
              <BottomNavContent>
                <BottomNavMoreViews
                  classIdentifier="trim-features-modal"
                  location={location}
                  displayJellyImg={true}
                  displayMoreViewsLbl={true}
                  moreViewsLbl={
                    <Translate value="more.views.v3" /> || 'More Views'
                  }
                  analyticsKey={`${trimInfo && trimInfo.trimName}:bottom bar`}
                />
              </BottomNavContent>
              <BottomNavButtonControls>
                <BottomNavPrimaryButton
                  onClick={() => {
                    if (!selected) {
                      onSelect(style);
                    }
                    setIsClosing(true, onCloseModal(onClose));
                  }}
                  analyticsKey={`${trimInfo && trimInfo.trimName}:bottom bar`}
                  label={
                    selected || isPreSelected
                      ? translate('byo.page.general.ok')
                      : translate('component.trim.features.select')
                  }
                  includeIcon={false}
                />
              </BottomNavButtonControls>
            </BottomNav>
          )}
        </ModalCustomContent>
      </Modal>
      <Routes>
        <Route
          path="/package/:id"
          element={
            <SimpleOptionDetails
              options={trimHighlights}
              onClose={onDetailsClose}
              onlyCloseButton={true}
            />
          }
        />
        <Route
          path="/option/:id"
          element={
            <SimpleOptionDetails
              options={trimHighlights}
              onClose={onDetailsClose}
              onlyCloseButton={true}
            />
          }
        />
      </Routes>
    </>
  );
};

export default withTranslate(TrimFeaturesModal);
